import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useAgents } from "@/features/kyc/api/use-agents";
import type { AgentFilter } from "@/features/kyc/api/dto";
import {
  AgentFormDialog,
  agentsColumns,
  type AgentRow,
} from "@/features/kyc/components";

/** Traduit les filtres de colonnes DataTableClar en `AgentFilter` (§4.2). */
const toAgentFilter = (filters: ColumnFiltersState): AgentFilter => {
  const filter: AgentFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "agencyCode") filter.agencyCode = value;
    if (id === "agentType") filter.agentType = value as AgentFilter["agentType"];
    if (id === "status") filter.status = value as AgentFilter["status"];
  }
  return filter;
};

/** Liste paginée des agents — enrôlement et gestion des statuts (POST /Agents/all). */
export function AgentsPage() {
  const { t } = useTypedTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toAgentFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useAgents(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("kyc.agents.title")}
        </h1>
        {/* Création d'agent : rôles back-office uniquement (guide §6.2). */}
        <RequireRole roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("kyc.agents.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []) as AgentRow[]}
        columnsClar={agentsColumns}
        title={t("kyc.agents.title")}
        exportFileName="agents"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <AgentFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
